// src/lib/firebase.ts
// Firebase client SDK initialisation and auth helpers

import { initializeApp } from 'firebase/app'
import {
  type Auth,
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged as onFirebaseAuthStateChanged,
} from 'firebase/auth'
import type { User as FirebaseUser } from 'firebase/auth'
import { getDevAuthToken, getDevAuthUser, isDevAuthBypassEnabled } from './dev-auth'

export interface User {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
}

function getClientEnv() {
  const env = import.meta.env as Record<string, string | boolean | undefined>
  const readString = (key: string) => (typeof env[key] === 'string' ? env[key] : undefined)
  return {
    NODE_ENV: import.meta.env.PROD ? 'production' : 'development',
    DEV_AUTH_BYPASS: readString('DEV_AUTH_BYPASS'),
    VITE_DEV_AUTH_BYPASS: readString('VITE_DEV_AUTH_BYPASS'),
    DEV_AUTH_UID: readString('DEV_AUTH_UID'),
    VITE_DEV_AUTH_UID: readString('VITE_DEV_AUTH_UID'),
    DEV_AUTH_EMAIL: readString('DEV_AUTH_EMAIL'),
    VITE_DEV_AUTH_EMAIL: readString('VITE_DEV_AUTH_EMAIL'),
    DEV_AUTH_DISPLAY_NAME: readString('DEV_AUTH_DISPLAY_NAME'),
    VITE_DEV_AUTH_DISPLAY_NAME: readString('VITE_DEV_AUTH_DISPLAY_NAME'),
    DEV_AUTH_PHOTO_URL: readString('DEV_AUTH_PHOTO_URL'),
    VITE_DEV_AUTH_PHOTO_URL: readString('VITE_DEV_AUTH_PHOTO_URL'),
  }
}

function toAppUser(user: FirebaseUser): User {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
  }
}

const devAuthEnv = getClientEnv()
const devAuthEnabled = isDevAuthBypassEnabled(devAuthEnv)

// Firebase web config for ken-prj. These values are PUBLIC by design — they
// ship in every client bundle; access is gated by Firebase Auth + Firestore
// rules — so they are hardcoded here on purpose. A misconfigured .env once
// shipped `dev-bypass` as the API key to production and broke sign-in, so the
// real config is the source of truth and the dev placeholder is rejected.
const PROD_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBaf87fp7-Zf9--4nBvGLRpzEfNvdq0sTc',
  authDomain: 'ken-prj.firebaseapp.com',
  projectId: 'ken-prj',
}

function resolveFirebaseConfig() {
  const envKey = import.meta.env.VITE_FIREBASE_API_KEY
  // Ignore the dev placeholder / empty key — always fall back to real config.
  if (!envKey || envKey === 'dev-bypass') return PROD_FIREBASE_CONFIG
  return {
    apiKey: envKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || PROD_FIREBASE_CONFIG.authDomain,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || PROD_FIREBASE_CONFIG.projectId,
  }
}

const firebaseConfig = resolveFirebaseConfig()

// Always initialize the Firebase app (needed for Firestore even when auth is bypassed)
const app = initializeApp(firebaseConfig)
const auth: Auth | null = devAuthEnabled ? null : getAuth(app)

const googleProvider = devAuthEnabled ? null : new GoogleAuthProvider()
googleProvider?.setCustomParameters({ hd: 'globalcomix.com' })

/**
 * Opens the Google sign-in popup.
 * Rejects if the signed-in email is not @globalcomix.com.
 */
export async function signInWithGoogle(): Promise<User> {
  if (devAuthEnabled) {
    return getDevAuthUser(devAuthEnv)
  }

  if (!auth || !googleProvider) {
    throw new Error('Firebase auth is not initialized')
  }

  const result = await signInWithPopup(auth, googleProvider)
  const email = result.user.email ?? ''
  if (!email.endsWith('@globalcomix.com')) {
    await firebaseSignOut(auth)
    throw new Error('Only @globalcomix.com accounts are allowed.')
  }
  return toAppUser(result.user)
}

/** Sign the current user out. */
export async function signOut(): Promise<void> {
  if (devAuthEnabled || !auth) return
  await firebaseSignOut(auth)
}

/** Promise that resolves once Firebase has restored (or rejected) the persisted session. */
const authReady: Promise<void> = auth ? auth.authStateReady() : Promise.resolve()

/** Return the current user's Firebase ID token, or null if not signed in. */
export async function getIdToken(): Promise<string | null> {
  if (devAuthEnabled) {
    return getDevAuthToken(devAuthEnv)
  }

  await authReady
  const user = auth?.currentUser
  if (!user) return null
  return user.getIdToken()
}

export function onAuthStateChanged(
  authInstance: Auth | null,
  callback: (user: User | null) => void,
): () => void {
  if (devAuthEnabled) {
    callback(getDevAuthUser(devAuthEnv))
    return () => {}
  }

  if (!authInstance) {
    callback(null)
    return () => {}
  }

  return onFirebaseAuthStateChanged(authInstance, (user) => {
    callback(user ? toAppUser(user) : null)
  })
}

export { auth }
export const isDevAuthBypass = devAuthEnabled
